/**
 * WHAT A RESERVATION'S STATUS MEANS — said once, for every screen that asks.
 *
 * Three files decided this separately until now: the calendar (`calendar/reservations.js`), the
 * home-status reader (`property/homeStatus.js`) and the OTA price (`leads/otaPrice.js`). Two of
 * them held the same four words and the third held two others, and the next person to widen the
 * vocabulary would have found two of the three. That is the defect this repo already has a name
 * for — the same number worked out in five places (project doc 104).
 *
 * WHY IT IS BEING WIDENED NOW, WHEN NOTHING SENDS THESE WORDS YET. Guesty's own reservations carry
 * `inquiry` (4,923 of them on 2026-10-13), `declined`, `expired`, `reserved`, `closed` and
 * `awaiting_payment`. Our connector flattens all six into `pending`, `canceled` and `confirmed`
 * before they reach the database, which is why the word "inquiry" appears nowhere in this product
 * — the stage this product is mostly about (SAOS #225).
 *
 * Undoing that flattening is a change to a column six views and three screens read. So it is done
 * in two steps, and this file is the first: EVERY READER LEARNS THE WHOLE VOCABULARY WHILE THE NEW
 * WORDS STILL CANNOT ARRIVE. Nothing changes today — `inquiry` is not in the database yet, so no
 * row moves — and when the connector stops flattening, every reader here is already right.
 *
 * The mapping each new word has to honour is the one it has today, so that nothing moves when it
 * stops being flattened:
 *
 *     inquiry          → was `pending`    → nobody is staying
 *     declined         → was `canceled`   → nobody is staying
 *     expired          → was `canceled`   → nobody is staying
 *     closed           → was `unknown`    → nobody is staying
 *     awaiting_payment → was `unknown`    → nobody is staying
 *     reserved         → was `confirmed`  → somebody is staying, and the money is ours
 */

/**
 * NOBODY IS STAYING. An enquiry, a cancellation, a refusal, a lapse, a no-show.
 *
 * This is the set the CALENDAR is built on, and getting it wrong is not a cosmetic mistake: a night
 * held by a reservation is a night the search page will not sell and the Listing shows as booked.
 * On 2026-10-13 there were 4,923 enquiries; if they ever count as stays, several thousand nights go
 * dark for homes that are empty.
 *
 * `cancelled` with two Ls is Cloudbeds' spelling and is kept beside Guesty's.
 */
/*
  NOT EXPORTED, and `Object.freeze` is not used either — it does nothing to a Set, which a test in
  this repo proved by calling `.add` on a "frozen" one and quietly breaking the test after it. The
  vocabulary is reached only through the two functions below, so there is nothing to widen in
  passing and nothing for a caller to mutate.
*/
const NOT_A_STAY = new Set([
  'pending', 'canceled', 'cancelled', 'no_show',
  /* Not yet emitted — see the file comment. They mean the same as the words above them. */
  'inquiry', 'declined', 'expired', 'closed', 'awaiting_payment',
])

/**
 * MONEY WE ACTUALLY HAVE, as against a price an OTA quoted somebody who has not booked.
 *
 * `reserved` belongs here because it already does: the connector turns it into `confirmed` today,
 * so those twenty-six reservations count as booked, and they must keep counting when the word
 * stops being rewritten.
 */
const MONEY_IN_HAND = new Set(['confirmed', 'completed', 'reserved'])

const word = (status) => String(status ?? '').trim().toLowerCase()

/** Is somebody staying on this reservation? An unknown word is treated as a stay, as it is today. */
export const isStay = (status) => !NOT_A_STAY.has(word(status))

/** Is this reservation money we have, rather than a price somebody was quoted? */
export const isBooked = (status) => MONEY_IN_HAND.has(word(status))
